import {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useState,
} from "react";
import { ChatbotBlock } from "./Chatbot/schema";
import { cloneDeep, merge, noop } from "lodash";

type BlockId = Required<ChatbotBlock["config"]>["id"];

type Ctx = Record<BlockId, ChatbotBlock["config"]>;

const DraftChatbotBlockContext = createContext<Ctx>({});
const UpdateDraftChatbotBlockContext =
  createContext<
    (
      id: BlockId,
      config: Partial<ChatbotBlock["config"]>,
      remove?: boolean
    ) => void
  >(noop);

export function DraftChatbotBlockProvider({
  children,
}: {
  children: ReactNode;
}) {
  const [draft, setDraft] = useState<Ctx>({});

  const updateDraft = useCallback(
    (
      id: BlockId,
      config: Partial<ChatbotBlock["config"]>,
      remove?: boolean
    ) => {
      setDraft((drafts) => {
        const oldDraft = cloneDeep(drafts[id]);
        const newDraft = merge(oldDraft, config);

        if (remove) {
          delete drafts[id];
          return drafts;
        }

        return {
          ...drafts,
          [id]: newDraft,
        };
      });
    },
    []
  );

  return (
    <UpdateDraftChatbotBlockContext.Provider value={updateDraft}>
      <DraftChatbotBlockContext.Provider value={draft}>
        {children}
      </DraftChatbotBlockContext.Provider>
    </UpdateDraftChatbotBlockContext.Provider>
  );
}

export function useDraftChatbotBlock(id?: BlockId) {
  const drafts = useContext(DraftChatbotBlockContext);
  return id ? drafts[id] : undefined;
}

export function useUpdateDraftChatbotBlock() {
  return useContext(UpdateDraftChatbotBlockContext);
}
